import React from "react";
import {
  Box,
  Card,
  CardActionArea,
  Typography,
  Grid,
  alpha,
  useTheme,
  Stack,
  Radio,
} from "@mui/material";
import CorporateFareRoundedIcon from "@mui/icons-material/CorporateFareRounded";
import Diversity3RoundedIcon from "@mui/icons-material/Diversity3Rounded";
import PlaceRoundedIcon from "@mui/icons-material/PlaceRounded";
import { useFormikContext } from "formik";
import { BusinessFormValues } from "./BussinessAccounRegister";

/* -------------------------------------------------------------
 * Brand / Design Tokens (StageLink)
 * ----------------------------------------------------------- */
const GRADIENT = "linear-gradient(90deg, #6366F1 0%, #9F03FF 100%)"; // primary brand gradient
const GRADIENT_START = "#6366F1"; // for icons / bullets

/* -------------------------------------------------------------
 * Tier metadata – with rich capability bullets
 * ----------------------------------------------------------- */
const TIERS = [
  {
    value: "CTP" as const,
    title: "Corporate (CTP)",
    summary: "Brands, NGOs & Associations",
    icon: CorporateFareRoundedIcon,
    bullets: [
      "Create multiple talent profiles",
      "Launch branded campaigns",
      "Company‑wide analytics & wallet",
    ],
  },
  {
    value: "PTP" as const,
    title: "Partner Agency (PTP)",
    summary: "Entertainment agencies & Institutions",
    icon: Diversity3RoundedIcon,
    bullets: [
      "Represent & manage talents",
      "Priority booking dashboard",
      "Commission tracking & reports",
    ],
  },
  {
    value: "VTP" as const,
    title: "Venue (VTP)",
    summary: "Cafés, Bars & Event Spaces",
    icon: PlaceRoundedIcon,
    bullets: [
      "Create venue listings & schedules",
      "Sell tickets & manage capacity",
      "Event‑specific analytics",
    ],
  },
];

/**
 * Step 1 – Tier Selection with gradient styling and Lexend typography.
 */

const StepOneBusinessType: React.FC = () => {
  const theme = useTheme();
  const {
    values,
    handleChange,
    setFieldValue,
    touched,
    errors,
    setFieldError,
  } = useFormikContext<BusinessFormValues>();
  const selected = values.businessType;

  /* Hover & selected backgrounds */
  const hoverBG = alpha(GRADIENT_START, 0.06);
  const selectedBG = alpha(GRADIENT_START, 0.12);

  return (
    <Box>
      <Typography
        variant="h6"
        mb={3}
        fontFamily="Lexend, sans-serif"
        fontWeight={700}
        textAlign="center"
        sx={{
          background: GRADIENT,
          WebkitBackgroundClip: "text",
          color: "transparent",
        }}
      >
        1️⃣ Choose Your Business Type
      </Typography>

      <Grid container spacing={2} alignItems="stretch">
        {TIERS.map(({ value, title, summary, icon: Icon, bullets }) => {
          const isActive = selected === value;
          return (
            <Grid item xs={12} sm={4} key={value} display="flex">
              <Radio
                name="businessType"
                value={value}
                checked={isActive}
                onChange={() => {
                  setFieldValue("businessType", value);
                  setFieldError("businessType", undefined); // Reset error
                }}
                sx={{ display: "none" }}
              />

              <Card
                elevation={isActive ? 8 : 2}
                sx={{
                  flexGrow: 1,
                  minHeight: 280,
                  display: "flex",
                  flexDirection: "column",
                  borderRadius: theme.shape.borderRadius * 1.5,
                  border: isActive
                    ? "none"
                    : `1px solid ${theme.palette.divider}`,
                  background: isActive ? GRADIENT : "background.paper",
                  color: isActive ? "#fff" : "inherit",
                  "&:hover": {
                    boxShadow: theme.shadows[6],
                    background: isActive ? GRADIENT : hoverBG,
                  },
                }}
              >
                <CardActionArea
                  sx={{
                    p: 3,
                    display: "flex",
                    flexDirection: "column",
                    height: "100%",
                  }}
                  onClick={() => {
                    setFieldValue("businessType", value);
                    setFieldError("businessType", undefined); // Reset error
                  }}
                >
                  <Stack
                    spacing={1}
                    alignItems="center"
                    textAlign="center"
                    sx={{ mb: 2 }}
                  >
                    <Icon
                      sx={{
                        fontSize: 48,
                        color: isActive ? "#fff" : GRADIENT_START,
                      }}
                    />
                    <Typography
                      variant="subtitle1"
                      fontWeight={700}
                      fontFamily="Lexend, sans-serif"
                    >
                      {title}
                    </Typography>
                    <Typography
                      variant="body2"
                      color={isActive ? "#fff" : "text.secondary"}
                    >
                      {summary}
                    </Typography>
                  </Stack>

                  {/* Capability bullets */}
                  <Stack
                    component="ul"
                    spacing={0.5}
                    sx={{ listStyle: "none", pl: 0, mt: "auto" }}
                  >
                    {bullets.map((b) => (
                      <Typography
                        component="li"
                        key={b}
                        variant="caption"
                        fontFamily="Lexend, sans-serif"
                        sx={{
                          "&::before": {
                            content: "'•'",
                            mr: 0.5,
                            color: isActive ? "#fff" : GRADIENT_START,
                          },
                        }}
                      >
                        {b}
                      </Typography>
                    ))}
                  </Stack>
                </CardActionArea>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {touched.businessType && errors.businessType && (
        <Typography
          color="error"
          variant="caption"
          sx={{ mt: 1, display: "block", fontFamily: "Lexend, sans-serif" }}
        >
          {errors.businessType}
        </Typography>
      )}
    </Box>
  );
};

export default StepOneBusinessType;
